/**
 * ChatInput Component
 * Input field for sending chat messages with auto-resize and keyboard shortcuts
 * Modern ChatGPT-inspired design with rounded corners
 */

import { useState, useRef, KeyboardEvent, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Send, StopCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { TopicSelector } from "./TopicSelector";

interface ChatInputProps {
  onSubmit: (text: string) => void;
  disabled?: boolean;
  isStreaming?: boolean;
  onStopGeneration?: () => void;
  placeholder?: string;
  className?: string;
  value?: string; // Controlled value
  onValueChange?: (value: string) => void; // Controlled onChange
  compact?: boolean; // Compact mode for Aside
}

export const ChatInput = ({
  onSubmit,
  disabled,
  isStreaming,
  onStopGeneration,
  placeholder = "Escribe tu mensaje...",
  className,
  value: controlledValue,
  onValueChange,
  compact = false,
}: ChatInputProps) => {
  const [internalInput, setInternalInput] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Use controlled value if provided, otherwise use internal state
  const input = controlledValue !== undefined ? controlledValue : internalInput;
  const setInput =
    onValueChange !== undefined ? onValueChange : setInternalInput;

  // Auto-focus on mount
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [input]);

  const handleSubmit = () => {
    if (!input.trim() || disabled) return;
    onSubmit(input);
    setInput("");

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleStopGeneration = () => {
    onStopGeneration?.();
  };

  return (
    <div className={cn("bg-background", compact ? "p-2" : "p-4", className)}>
      <div className={cn(!compact && "max-w-4xl mx-auto")}>
        {/* Input Container */}
        <div
          className={cn(
            "flex items-center transition-all",
            compact ? "gap-2 p-2 rounded-2xl" : "gap-3 p-4 rounded-3xl",
            "border-2 border-border bg-background",
            "focus-within:border-primary focus-within:shadow-lg",
            "hover:border-border-hover"
          )}
        >
          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            className={cn(
              "flex-1 resize-none bg-transparent",
              "text-foreground placeholder:text-text-secondary",
              "focus:outline-none",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              "py-0.5",
              compact
                ? "text-sm min-h-[20px] max-h-[120px]"
                : "min-h-[24px] max-h-[200px]"
            )}
          />

          {/* Send/Stop Button */}
          <div className="flex-shrink-0">
            {isStreaming ? (
              <Button
                variant="destructive"
                onClick={handleStopGeneration}
                size="icon"
                className={cn(
                  "rounded-full",
                  compact ? "h-8 w-8" : "h-10 w-10"
                )}
              >
                <StopCircle className={cn(compact ? "h-4 w-4" : "h-5 w-5")} />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={disabled || !input.trim()}
                size="icon"
                className={cn(
                  "rounded-full transition-opacity duration-200",
                  compact ? "h-8 w-8" : "h-10 w-10",
                  input.trim()
                    ? "bg-gradient hover:opacity-90"
                    : "bg-muted hover:bg-muted"
                )}
              >
                <Send
                  className={cn(
                    "transition-colors",
                    compact ? "h-4 w-4" : "h-5 w-5",
                    input.trim() ? "text-white" : "text-text-secondary"
                  )}
                />
              </Button>
            )}
          </div>
        </div>

        {/* Helper Text and Topic Selector - Only show in full mode */}
        {!compact && (
          <div className="mt-3 flex items-center justify-between">
            {/* Topic Selector */}
            <TopicSelector onTopicSelect={setInput} />

            {/* Keyboard Shortcuts */}
            <div className="text-xs text-text-secondary">
              <kbd className="px-2 py-1 bg-muted rounded-md text-[10px] font-mono">
                Enter
              </kbd>{" "}
              enviar •{" "}
              <kbd className="px-2 py-1 bg-muted rounded-md text-[10px] font-mono">
                Shift + Enter
              </kbd>{" "}
              nueva línea
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
